import React, { useState } from 'react';
import { notification } from 'components/ds';
import { useNavigate } from 'react-router-dom';
import PageContainer from 'modules/account/components/PageContainer/PageContainer';
import { handleApiError } from 'components/ErrorHandler';
import { useGetTemplate } from '../../../configs/SchemaConfig';
import { pageConfigs } from '../../../configs/pageConfigs';
import { PasswordForm } from '../../../components/PasswordForm/PasswordForm';
import { usePassword } from '../../../hooks/usePassword';
import { useOtpStorage } from '../../../hooks/useOtpStorage';
import { RouteEnum } from '../../../constantes/RouteEnum';
import TurnstileCaptcha from 'components/TurnstileCaptcha';
import { useGAEvents } from '../../../../../hooks/useGoogleAnalytics';

interface PasswordFormValues {
  newPassword: string;
  confirmPassword: string;
}

export const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const {data: pageConfig = {}} = useGetTemplate(pageConfigs.forgotPassword.template);
  const {recoveryPassword, isLoading} = usePassword();
  const {otpId, otpCode} = useOtpStorage()
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const { events } = useGAEvents();
  
  // Evento GA4: Início do formulário de reset de senha
  React.useEffect(() => {
    events.formStart('reset_password');
  }, [events]);
  
  const handleVerify = (token: string) => {
    setCaptchaToken(token);
  }
  
  const handleResetPassword = async ({newPassword}: PasswordFormValues) => {
    if (!otpId || !otpCode) {
      events.error('reset_password_validation_error', 'Informações de validação ausentes');
      notification.error({
        message: 'Erro na validação',
        description: 'Informações de validação ausentes. Por favor, solicite um novo código.'
      });
      
      return;
    }
    
    if (!captchaToken) {
      events.error('reset_password_captcha_error', 'Verificação de segurança não completada');
      notification.error({
        message: 'Verificação necessária',
        description: 'Por favor, complete a verificação de segurança.'
      });
      
      return;
    }
    
    try {
      const success = await recoveryPassword({
        otpId,
        otpCode,
        newPassword,
        captchaToken
      });
      
      if (success) {
        // Evento GA4: Sucesso no reset de senha
        events.formSubmit('reset_password', true);
        navigate(RouteEnum.LOGIN_USERNAME);
      }
    } catch (error: any) {
      setCaptchaToken('');
      setCaptchaKey(prev => prev + 1);
      
      // Evento GA4: Erro no reset de senha
      events.formSubmit('reset_password', false);
      events.error('reset_password_error', error?.message || 'Erro ao redefinir senha');
      
      handleApiError(error, 'Não foi possível redefinir sua senha');
    }
  };
  
  return (
    <PageContainer
      title={pageConfig.title}
      navigateBack={pageConfig?.navigateBack?.()}
      disabled={isLoading || !captchaToken}
    >
      <PasswordForm
        isLoading={isLoading}
        buttonText="Redefinir senha"
        onSubmit={handleResetPassword}
        captchaComponent={<TurnstileCaptcha onVerify={handleVerify} key={captchaKey}/>}
        disableSubmit={!captchaToken}
      />
    </PageContainer>
  );
};
